/* eslint-disable ember/no-computed-properties-in-native-classes */
import EmberObject, { computed } from '@ember/object';

import * as CommonUtils from '../../../utils/common';
import FirstOrderStage from '../../stages/first-order';
import { numberToUserDisplay } from '@ebiz/pretty-num';

export default class StageDetailResults extends EmberObject {
  targetStage = null;
  actualStage = null;
  interestingCircuitResult = null;
  circuitResult = null;
  graphData = null;
  whoAmI = 'app/models/results/sub-results/stage-details.js';

  @computed('targetStage.stageLetter') get stageLetter() {
    return this.targetStage.stageLetter;
  }
  @computed('actualStage.selectedImplementation') get implementation() {
    return this.actualStage.selectedImplementation;
  } // e.g: FilterConstants.implementationTypes.sallenKey
  @computed('targetStage.filter.name') get filterName() {
    var filterName = this.targetStage.filter.name;
    var result = CommonUtils.displayStageType(filterName);
    return result;
  }

  @computed('targetStage.order') get nthOrder() {
    var order = this.targetStage.order;
    var result = CommonUtils.toOrdinal(order);
    return result;
  }

  @computed('interestingCircuitResult') get interestingCircuitStageValues() {
    var interestingCircuitResult = this.interestingCircuitResult;
    if (!interestingCircuitResult) {
      return null;
    }

    var stageValuesArray = interestingCircuitResult.map((item) => {
      return item.stageValues;
    });
    return stageValuesArray;
  }

  @computed('circuitResult.stageValues.gain', 'interestingCircuitStageValues') get toleranceGain() {
    var interestingCircuitStageValues = this.interestingCircuitStageValues;
    if (!interestingCircuitStageValues) {
      return { minimum: null, maximum: null };
    }

    var nominalGain = this.circuitResult.stageValues.gain;
    var gains = interestingCircuitStageValues.map((item) => {
      return item.gain;
    });
    gains.push(nominalGain);
    var minimum = Math.min.apply(Math, gains);
    var maximum = Math.max.apply(Math, gains);
    return { minimum: minimum, maximum: maximum };
  }

  @computed('actualStage.gain', 'targetStage.gain', 'toleranceGain') get gain() {
    var targetStageGain = this.targetStage.gain.toFixed(2);
    var actualGain = this.actualStage ? this.actualStage.gain : null;
    var toleranceGain = this.toleranceGain;
    var simulatedValue = actualGain ? actualGain.toFixed(2) : null;
    var result = {
      target: targetStageGain,
      simulatedValue: simulatedValue,
      simulatedFrom: toleranceGain.minimum,
      simulatedTo: toleranceGain.maximum,
    };
    return result;
  }

  @computed('actualStage.stageFp', 'interestingCircuitStageValues') get toleranceStageFp() {
    var interestingCircuitStageValues = this.interestingCircuitStageValues;
    if (!interestingCircuitStageValues) {
      return { minimum: null, maximum: null };
    }

    var nominalStageFp = this.actualStage.stageFp;
    var stageFpArray = interestingCircuitStageValues.map((item) => {
      return item.fp;
    });
    stageFpArray.push(nominalStageFp);
    var minimum = Math.min.apply(Math, stageFpArray);
    var maximum = Math.max.apply(Math, stageFpArray);
    return { minimum: minimum, maximum: maximum };
  }

  @computed('actualStage.stageFp', 'targetStage.stageFp', 'toleranceStageFp') get stageFp() {
    var value = this.targetStage.stageFp;
    var actualStageFp = this.actualStage ? this.actualStage.stageFp : null;
    var toleranceStageFp = this.toleranceStageFp;
    var targetStageFp = numberToUserDisplay(value, 'Engineering', 4, { showZeroes: false });
    var simulatedValue = actualStageFp ? actualStageFp.toPrecision(4) : null;
    var result = {
      target: targetStageFp,
      simulatedValue: simulatedValue,
      simulatedFrom: toleranceStageFp.minimum,
      simulatedTo: toleranceStageFp.maximum,
      enabled: true,
    };
    return result;
  }

  @computed('actualStage.stageQ', 'interestingCircuitStageValues') get toleranceStageQ() {
    var interestingCircuitStageValues = this.interestingCircuitStageValues;
    if (!interestingCircuitStageValues) {
      return { minimum: null, maximum: null };
    }

    var nominalStageQ = this.actualStage.stageQ;
    var stageQArray = interestingCircuitStageValues.map((item) => {
      return item.q;
    });
    stageQArray.push(nominalStageQ);
    var minimum = Math.min.apply(Math, stageQArray);
    var maximum = Math.max.apply(Math, stageQArray);
    return { minimum: minimum, maximum: maximum };
  }

  @computed('actualStage.stageQ', 'targetStage.stageQ', 'toleranceStageQ') get stageQ() {
    var value = this.targetStage.stageQ;
    var actualStageQ = this.actualStage ? this.actualStage.stageQ : null;
    var targetStage = this.targetStage;
    var toleranceStageQ = this.toleranceStageQ;
    var targetStageQ = numberToUserDisplay(value, 'Engineering', 4, { showZeroes: false });
    var simulatedValue = actualStageQ ? actualStageQ.toPrecision(4) : null;
    var enabled = true;
    if (targetStage instanceof FirstOrderStage) {
      enabled = false;
    }

    var result = {
      target: targetStageQ,
      simulatedValue: simulatedValue,
      simulatedFrom: toleranceStageQ.minimum,
      simulatedTo: toleranceStageQ.maximum,
      enabled: enabled,
    };
    return result;
  }
}
